import {
  getImageSourceFromFileSystem,
  renderIcon,
  ActionSelect,
  ActionSelectItem,
  ComponentBaseProps,
  Camera,
  imageResize,
  IMAGE_WIDTH,
  getPdfFromFileSystem,
  getImageSourceFromFileSystemOffline,
} from '@ecdlink/ui';
import { useCallback, useEffect, useState } from 'react';
import * as styles from './photo-prompt.styles';
import { PhotoPromptActionType } from './photo-prompt.types';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

export interface PhotoPromptProps extends ComponentBaseProps {
  title: string;
  resolutionLimit?: number;
  onClose?: () => void;
  onAction?: (imageBaseString: string) => void;
  onDelete?: () => void;
  shouldNotShowPdfOption?: boolean;
}

/**
 * Refactor proposal: Pass action list as subcomponent instead. This will remove the need to call the get actions method in the useEffect. HG
 *
 */

export const PhotoPrompt: React.FC<PhotoPromptProps> = ({
  title,
  resolutionLimit,
  onClose,
  onAction,
  onDelete,
  shouldNotShowPdfOption,
}) => {
  const [actions, setActions] = useState<
    ActionSelectItem<PhotoPromptActionType>[]
  >([]);
  const [isOpenCamera, setIsOpenCamera] = useState(false);
  const adjustedResolution = resolutionLimit ? resolutionLimit : 100;
  const { isOnline } = useOnlineStatus();

  const getActions = useCallback(() => {
    const actionsList: ActionSelectItem<PhotoPromptActionType>[] = [];

    if (onDelete) {
      actionsList.push({
        icon: renderIcon('TrashIcon', styles.iconStyle),
        title: 'Delete',
        value: 'delete',
        actionColour: 'errorMain',
      });
    }

    actionsList.push({
      icon: renderIcon('PhotographIcon', styles.iconStyle),
      title: 'Gallery',
      value: 'gallery',
    });

    actionsList.push({
      icon: renderIcon('CameraIcon', styles.iconStyle),
      title: 'Camera',
      value: 'camera',
    });

    if (!shouldNotShowPdfOption) {
      actionsList.push({
        icon: renderIcon('DocumentIcon', styles.iconStyle),
        title: 'Upload PDF',
        value: 'pdf',
      });
    }

    setActions(actionsList);
  }, [onDelete, shouldNotShowPdfOption]);

  const openPdfUpload = () => {
    getPdfFromFileSystem().then((pdfData) => {
      if (pdfData && onAction) {
        onAction(pdfData);
      }
    });
  };

  const openCamera = async () => {
    console.log('Open camera');
    setIsOpenCamera(true);
  };

  const openGallery = () => {
    if (isOnline) {
      getImageSourceFromFileSystem(resolutionLimit).then((dataUrl) => {
        if (dataUrl && onAction) {
          onAction(dataUrl);
        }
      });
    } else {
      getImageSourceFromFileSystemOffline(resolutionLimit).then((dataUrl) => {
        if (dataUrl && onAction) {
          onAction(dataUrl);
        }
      });
    }
  };

  const deletePhoto = () => {
    if (onDelete) {
      onDelete();
    }
  };

  const close = () => {
    if (onClose) {
      onClose();
    }
  };

  const onGetPhoto = (photo: string) => {
    if (onAction) {
      imageResize(photo, adjustedResolution, null, null).then((dataUrl) => {
        if (dataUrl) onAction(dataUrl);
      });
    }
  };

  const actionSelected = (value: PhotoPromptActionType) => {
    switch (value) {
      case 'camera':
        openCamera();
        break;
      case 'delete':
        deletePhoto();
        break;
      case 'gallery':
        openGallery();
        break;
      case 'pdf':
        openPdfUpload();
        break;
      default:
        close();
        break;
    }
  };

  useEffect(() => {
    getActions();
  }, [getActions]);

  return (
    <>
      <ActionSelect
        actions={actions}
        title={title}
        onActionSelected={actionSelected}
        onClose={close}
      />

      {isOpenCamera && <Camera onGetPhoto={onGetPhoto} onClose={close} />}
    </>
  );
};
